import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaBuilding, FaCheckCircle, FaMapMarkerAlt, FaSearch, FaTools } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { API_URL } from '../services/api';
import {
  MARKET_DATA,
  VENDOR_SPECIALTIES,
  getMarket,
  getSpecialty,
  getVendorQuery,
} from '../data/seoLandingData';
import { trackEvent } from '../utils/analytics';

interface Vendor {
  id: number;
  slug: string | null;
  name: string;
  city: string;
  state: string;
  specialty: string;
  description: string | null;
  is_featured: boolean;
  is_claimed: boolean;
  is_domain_confirmed?: boolean;
  active_job_count: number;
}

const specialtyByApi = Object.values(VENDOR_SPECIALTIES).reduce((acc, specialty) => {
  acc[specialty.apiValue] = specialty.label;
  return acc;
}, {} as Record<string, string>);

const getVendorPath = (vendor: Pick<Vendor, 'id' | 'slug'>) => `/vendors/${vendor.slug || vendor.id}`;

const getClaimHref = (vendor: Pick<Vendor, 'id' | 'slug'>) => {
  const redirect = `${getVendorPath(vendor)}?claim=1`;
  return `/register?${new URLSearchParams({ role: 'employer', redirect }).toString()}`;
};

const VendorSpecialtyCityPage: React.FC = () => {
  const { city, specialty: specialtySlug } = useParams<{ city: string; specialty: string }>();
  const market = getMarket(city);
  const specialty = getSpecialty(specialtySlug);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const pageUrl = useMemo(() => {
    if (!market || !specialty) return 'https://stagecrew.app/vendors';
    return `https://stagecrew.app/vendors/city/${market.slug}/${specialty.slug}`;
  }, [market, specialty]);

  useEffect(() => {
    if (!market || !specialty) return;
    setLoading(true);
    fetch(`${API_URL}/api/vendors?${getVendorQuery(market, specialty, 60)}`)
      .then(r => r.json())
      .then(data => {
        setVendors(data.vendors || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setVendors([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [market, specialty]);

  if (!market || !specialty) {
    return <Navigate to="/vendors" replace />;
  }

  const title = `${specialty.label} Companies in ${market.name}, ${market.stateAbbr} | StageCrew`;
  const description = `${specialty.description} Browse ${specialty.pluralLabel} in ${market.name}, ${market.stateAbbr}, compare local listings, and claim your company page.`;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title={title}
        description={description}
        url={pageUrl}
        keywords={`${specialty.label} companies ${market.name}, ${specialty.label} ${market.name}, ${market.name} ${specialty.pluralLabel}, event vendors ${market.name}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${specialty.pluralLabel} in ${market.name}`,
          description,
          url: pageUrl,
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: total,
            itemListElement: vendors.slice(0, 20).map((vendor, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: `https://stagecrew.app${getVendorPath(vendor)}`,
              name: vendor.name,
            })),
          },
        }}
      />

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/vendors" className="hover:text-blue-700">Vendor Directory</Link>
            <span>/</span>
            <Link to={`/vendors/city/${market.slug}`} className="hover:text-blue-700">{market.name}</Link>
            <span>/</span>
            <span>{specialty.label}</span>
          </div>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 mb-4">
              <FaMapMarkerAlt /> {market.name}, {market.stateAbbr}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-950 tracking-normal mb-4">
              {specialty.label} companies in {market.name}
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl">
              {specialty.description} StageCrew keeps these pages connected to real vendor listings and claim CTAs so business owners can keep their profiles accurate.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to={`/markets/${market.slug}`} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800">
                <FaSearch /> View market guide
              </Link>
              <Link to="/claim/vendor-listing" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                <FaCheckCircle /> Claim a listing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaBuilding className="text-blue-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">{total} local listings</h2>
          <p className="text-sm text-slate-600 mt-2">Filtered by {specialty.label.toLowerCase()} service and {market.name} coverage.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaTools className="text-emerald-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Buyer intent</h2>
          <p className="text-sm text-slate-600 mt-2">Built for searches around {specialty.buyerIntent}.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaCheckCircle className="text-amber-600 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Claim workflow</h2>
          <p className="text-sm text-slate-600 mt-2">Unclaimed listings include direct claim buttons that preserve the vendor page redirect.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-12">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2, 3, 4, 5].map(item => (
              <div key={item} className="rounded-lg border border-slate-200 bg-white p-6 animate-pulse">
                <div className="h-5 w-2/3 bg-slate-200 rounded mb-3" />
                <div className="h-4 w-1/2 bg-slate-100 rounded mb-4" />
                <div className="h-3 w-full bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
            <h2 className="text-xl font-bold text-slate-950">No {specialty.label.toLowerCase()} listings yet in {market.name}</h2>
            <p className="text-slate-600 mt-2">Browse all vendors in this market while this specialty page fills in.</p>
            <Link to={`/vendors/city/${market.slug}`} className="inline-flex mt-5 rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800">
              Browse {market.name} vendors
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {vendors.map(vendor => (
              <article key={vendor.id} className="rounded-lg border border-slate-200 bg-white p-5 hover:border-blue-400 hover:shadow-sm transition">
                <div className="flex flex-wrap gap-2 mb-3">
                  {vendor.active_job_count > 0 && <span className="rounded-lg bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Pay-listed jobs</span>}
                  {vendor.is_claimed ? (
                    <span className={`rounded-lg px-2 py-1 text-xs font-semibold ${
                      vendor.is_domain_confirmed
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {vendor.is_domain_confirmed
                        ? 'Business domain confirmed'
                        : 'Managed listing'}
                    </span>
                  ) : (
                    <span className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">Unclaimed</span>
                  )}
                </div>
                <Link to={getVendorPath(vendor)} className="text-lg font-bold text-slate-950 hover:text-blue-700">
                  {vendor.name}
                </Link>
                <p className="text-sm text-slate-500">{vendor.city}, {vendor.state}</p>
                <p className="text-sm font-semibold text-slate-700 mt-3">{specialtyByApi[vendor.specialty] || specialty.label}</p>
                {vendor.description && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{vendor.description}</p>}
                <div className="flex flex-wrap gap-2 mt-5">
                  <Link to={getVendorPath(vendor)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                    View profile
                  </Link>
                  {!vendor.is_claimed && (
                    <Link
                      to={getClaimHref(vendor)}
                      onClick={() => trackEvent('claim_cta_click', 'vendor_claim', 'specialty_city_card')}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      Claim this page
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-slate-950 mb-4">Related services in {market.name}</h2>
            <div className="flex flex-wrap gap-3">
              {market.topSpecialties.filter(slug => slug !== specialty.slug).map(slug => {
                const related = VENDOR_SPECIALTIES[slug];
                if (!related) return null;
                return (
                  <Link key={slug} to={`/vendors/city/${market.slug}/${related.slug}`} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
                    {related.label}
                  </Link>
                );
              })}
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-950 mb-4">Nearby markets</h2>
            <div className="flex flex-wrap gap-3">
              {market.nearbyMarkets.map(slug => {
                const nearby = MARKET_DATA[slug];
                if (!nearby) return null;
                return (
                  <Link key={slug} to={`/vendors/city/${nearby.slug}/${specialty.slug}`} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
                    {nearby.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VendorSpecialtyCityPage;
